precision highp float;

varying vec2 vUv;
uniform float uTime;
uniform vec2  uResolution;
uniform float uHover;
uniform float uAudioLevel;
uniform float uBass;
uniform float uMid;
uniform float uTreble;
uniform vec3  uObjectColor;
uniform vec3  uBgColor;
uniform float uRotSpeed;
uniform float uRotDir;
uniform int   uMandalIdx;
uniform float uScale;

#define PI 3.14159265359
#define TAU 6.28318530718

float hash(float n) { return fract(sin(n) * 43758.5453); }

float ring(vec2 p, float r, float t) {
    return smoothstep(t, 0.0, abs(length(p) - r));
}

void main() {
    vec2 uv = vUv - 0.5;
    uv.x *= uResolution.x / uResolution.y;
    uv /= max(uScale, 0.01);

    // Global rotation (Pro: uRotSpeed > 0)
    float rotAngle = uTime * uRotSpeed * uRotDir * 0.2;
    float cs = cos(rotAngle), sn = sin(rotAngle);
    uv = mat2(cs, -sn, sn, cs) * uv;

    float R     = 0.15;
    float outerR = 2.0 * R;
    float thick = 0.004 + uAudioLevel * 0.003 + uHover * 0.002;

    vec3 lineCol = uObjectColor * 0.55;
    vec3 glowCol = uObjectColor;
    vec3 dotCol  = min(uObjectColor + vec3(0.3), vec3(1.0));

    float lineMask = 0.0;
    float glowAcc  = 0.0;
    float dotAcc   = 0.0;

    float sigma  = R * (0.85 + uBass * 0.55);
    float cLen   = length(uv);
    float cGlow  = exp(-(cLen * cLen) / (2.0 * sigma * sigma)) * (0.28 + uBass * 0.50);

    // ── Mandala 0: Flower of Life ─────────────────────────────────────────
    if (uMandalIdx == 0) {
        vec2 c[19];
        c[0]  = vec2( 0.0,       0.0      );
        c[1]  = vec2( 1.0,       0.0      );  c[2]  = vec2( 0.5,  0.8660254);
        c[3]  = vec2(-0.5,       0.8660254);  c[4]  = vec2(-1.0,  0.0      );
        c[5]  = vec2(-0.5,      -0.8660254);  c[6]  = vec2( 0.5, -0.8660254);
        c[7]  = vec2( 2.0,       0.0      );  c[8]  = vec2( 1.5,  0.8660254);
        c[9]  = vec2( 1.0,       1.7320508);  c[10] = vec2( 0.0,  1.7320508);
        c[11] = vec2(-1.0,       1.7320508);  c[12] = vec2(-1.5,  0.8660254);
        c[13] = vec2(-2.0,       0.0      );  c[14] = vec2(-1.5, -0.8660254);
        c[15] = vec2(-1.0,      -1.7320508);  c[16] = vec2( 0.0, -1.7320508);
        c[17] = vec2( 1.0,      -1.7320508);  c[18] = vec2( 1.5, -0.8660254);

        for (int i = 0; i < 19; i++) {
            vec2  center = c[i] * R;
            vec2  p      = uv - center;
            float d      = length(p);
            lineMask = max(lineMask, ring(p, R, thick));
            glowAcc += exp(-abs(d - R) * 30.0) * 0.11 * (0.5 + uMid * 0.5);
            float ph  = hash(float(i) * 127.1) * TAU;
            float spd = 0.8 + hash(float(i) * 311.7) * 1.8;
            float raw = sin(uTime * spd + ph) * 0.5 + 0.5;
            float audioGate = smoothstep(0.05, 0.40, uAudioLevel);
            float pulse = pow(raw, 2.5) * audioGate * (0.3 + uAudioLevel * 0.7);
            pulse += uBass * 0.10 * (1.0 - min(1.0, length(center) / (2.0 * R))) * audioGate;
            float dotR = R * (0.09 + pulse * 0.14);
            dotAcc += smoothstep(dotR, dotR * 0.2, d) * (0.15 + pulse * 0.45)
                    + exp(-d / (R * 0.38)) * pulse * 0.50;
        }
        lineMask = max(lineMask, ring(uv, outerR + R * 0.08, thick * 0.75));
        lineMask = max(lineMask, ring(uv, outerR + R * 0.24, thick * 0.42));

    // ── Mandala 1: Star Yantra ────────────────────────────────────────────
    } else if (uMandalIdx == 1) {
        // 12 radial spokes at 30° intervals
        for (int si = 0; si < 12; si++) {
            float a    = float(si) * PI / 6.0;
            vec2  dir  = vec2(cos(a), sin(a));
            vec2  perp = vec2(-dir.y, dir.x);
            float proj = dot(uv, dir);
            float pd   = abs(dot(uv, perp));
            float inLen = smoothstep(R * 0.15, R * 0.3, proj)
                        * smoothstep(outerR * 1.05, outerR * 0.9, proj);
            lineMask = max(lineMask, smoothstep(thick * 1.8, 0.0, pd) * inLen);
            glowAcc += exp(-pd * 22.0) * 0.055 * inLen * (0.5 + uMid * 0.5);
        }
        // 3 concentric circles (inner, mid, outer)
        lineMask = max(lineMask, ring(uv, R,        thick * 0.9));
        lineMask = max(lineMask, ring(uv, R * 2.0,  thick * 0.75));
        lineMask = max(lineMask, ring(uv, outerR,   thick * 0.6));
        // 6 node circles at spoke intersections with middle ring
        for (int ni = 0; ni < 6; ni++) {
            float a = float(ni) * PI / 3.0;
            vec2  cp = vec2(cos(a), sin(a)) * R * 2.0;
            lineMask = max(lineMask, ring(uv - cp, R * 0.28, thick * 0.55));
            dotAcc += exp(-length(uv - cp) / (R * 0.18)) * (0.08 + uBass * 0.25);
        }
        dotAcc += cGlow * 1.2;

    // ── Mandala 2: Nested Rings ───────────────────────────────────────────
    } else if (uMandalIdx == 2) {
        // Flower of Life positions but 3 concentric rings per center
        vec2 c[7];
        c[0] = vec2( 0.0,       0.0      );
        c[1] = vec2( 1.0,       0.0      );  c[2] = vec2( 0.5,  0.8660254);
        c[3] = vec2(-0.5,       0.8660254);  c[4] = vec2(-1.0,  0.0      );
        c[5] = vec2(-0.5,      -0.8660254);  c[6] = vec2( 0.5, -0.8660254);

        for (int i = 0; i < 7; i++) {
            vec2  center = c[i] * R;
            vec2  p      = uv - center;
            float d      = length(p);
            lineMask = max(lineMask, ring(p, R,        thick));
            lineMask = max(lineMask, ring(p, R * 0.62, thick * 0.72));
            lineMask = max(lineMask, ring(p, R * 0.28, thick * 0.50));
            glowAcc += exp(-abs(d - R)        * 28.0) * 0.08 * (0.5 + uMid * 0.5);
            glowAcc += exp(-abs(d - R * 0.62) * 32.0) * 0.05 * (0.5 + uMid * 0.5);
            float audioGate = smoothstep(0.05, 0.40, uAudioLevel);
            float pulse = pow(sin(uTime * (0.8 + hash(float(i) * 311.7) * 1.8)
                          + hash(float(i) * 127.1) * TAU) * 0.5 + 0.5, 2.5)
                          * audioGate * (0.3 + uAudioLevel * 0.7);
            float dotR = R * (0.09 + pulse * 0.14);
            dotAcc += smoothstep(dotR, dotR * 0.2, d) * (0.12 + pulse * 0.4);
        }
        // Outer double ring
        lineMask = max(lineMask, ring(uv, outerR,           thick * 0.7));
        lineMask = max(lineMask, ring(uv, outerR + R * 0.2, thick * 0.4));

    // ── Mandala 3: Archimedean Spiral ─────────────────────────────────────
    } else {
        float r      = length(uv);
        float theta  = atan(uv.y, uv.x) / TAU + 0.5;      // 0 → 1
        float rotPh  = uTime * uRotSpeed * uRotDir * 0.08; // spiral self-rotation
        float pitch  = 0.045;
        float phase  = fract(r / pitch - theta - rotPh);
        float sdist  = min(phase, 1.0 - phase) * pitch;
        float fade   = smoothstep(outerR + R * 0.5, R * 0.4, r);
        lineMask = smoothstep(thick * 2.2, 0.0, sdist) * fade;
        glowAcc  = exp(-sdist * 22.0) * 0.18 * (0.5 + uMid * 0.5) * fade;
        dotAcc   = cGlow * 1.5;
    }

    float clip = smoothstep(outerR + R * 0.6, outerR - R * 0.15, cLen);

    vec3 col = uBgColor;
    col += lineCol * lineMask * clip;
    col += glowCol * glowAcc  * clip;
    col += dotCol  * min(dotAcc, 1.8) * clip;
    if (uMandalIdx != 3) col += glowCol * 0.65 * cGlow;
    col *= 1.0 + uHover * 0.28;

    gl_FragColor = vec4(col, 1.0);
}
